import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { LoadingController, ToastController } from "@ionic/angular";
import { setFormErrors, validateAllFields } from "src/app/utils/forms";

@Injectable({
  providedIn: "root",
})
export class FormManager {
  constructor(
    private toastCtrl: ToastController,
    private loadingCtrl: LoadingController
  ) {}

  toastDuration: number = 5000;
  toastNonFieldErrors: boolean = true;

  // prettier-ignore
  public async submit(form: FormGroup, submitFn: () => Promise<void>, toast?: string): Promise<void> {
    await this.validateForm(form);
    if (form.valid) {
      let loadingView = await this.loadingCtrl.create({ message: "loading..." });
      await loadingView.present();
      try {
        await submitFn();
        if (toast) await this.openToast(toast);
        await loadingView.dismiss();
      } catch (err) {
        await loadingView.dismiss();
        await this.handleError(form, err as HttpErrorResponse);
        throw err;
      }
    } else {
      throw "Invalid form.";
    }
  }

  public async validateForm(form: FormGroup): Promise<void> {
    validateAllFields(form);
  }

  protected async handleError(form: FormGroup, e: HttpErrorResponse) {
    switch (e.status) {
      case 400: {
        setFormErrors(form, e.error);
        if (e.error.non_field_errors) {
          await this.handleNonFieldErrors(e.error.non_field_errors);
        }
        if (e.error && e.error?.error) {
          await this.openToast(e.error?.error, "danger");
        } else if (e.error && e.error?.errorMessage) {
          await this.openToast(e.error?.errorMessage, "danger");
        } else if (e.error && e.error.title) {
          await this.openToast(e.error.title, "danger");
        } else if (e.error && !e.error.error) {
          await this.openToast(e.error, "danger");
        }
        break;
      }

      case 404: {
        if (e && e.error && e.error?.error) {
          await this.openToast(e.error?.error, "danger");
        } else if (e && e.error && e.error?.errorMessage) {
          await this.openToast(e.error?.errorMessage, "danger");
        } else {
          await this.openToast(
            "Looks like the page you're trying to visit does not exist. Please check the URL and try again.",
            "danger"
          );
        }
        break;
      }

      case 409: {
        if (e && e.error && e.error?.error) {
          await this.openToast(e.error?.error, "danger");
        } else if (e && e.error && e.error?.errorMessage) {
          await this.openToast(e.error?.errorMessage, "danger");
        } else {
          await this.openToast(
            "We are unable to process your request at this time. Please try again later",
            "danger"
          );
        }
        break;
      }

      case 403: {
        await this.openToast(e.error?.detail, "danger");
        break;
      }

      case 500:
        await this.openToast(
          "Oops! Something went wrong on our server. Please try again later.",
          "danger"
        );
        break;

      case 429:
        await this.openToast(
          "Sorry, you've made too many requests. Please wait a while and try again.",
          "danger"
        );
        break;

      case 0: {
        await this.openToast(
          "Looks like you're not connected to the internet. Please check your connection and try again.",
          "danger"
        );
        break;
      }

      default: {
        console.error(e);
      }
    }
  }

  protected async handleNonFieldErrors(messages: string[]) {
    if (this.toastNonFieldErrors) {
      for (let msg of messages) {
        await this.openToast(msg);
      }
    }
  }

  async openToast(message: string, color: string = "primary") {
    if (message) {
      let toast = await this.toastCtrl.create({
        message: message,
        buttons: [{ text: "Done", role: "cancel" }],
        duration: this.toastDuration,
        color: color,
      });
      await toast.present();
    }
  }
}
